import type { GroupSchedule } from './types'

/**
 * Правка расписания группы (S06, этап 3 шаг 8): чистая логика слотов.
 * Слот - день недели (0 = воскресенье, как в groups.schedule), начало и конец
 * HH:MM и необязательный зал.
 */

/** Дни в порядке показа: неделя начинается с понедельника. */
export const WEEK: { day: number; short: string; long: string; on: string }[] = [
  { day: 1, short: 'Пн', long: 'Понедельник', on: 'в понедельник' },
  { day: 2, short: 'Вт', long: 'Вторник', on: 'во вторник' },
  { day: 3, short: 'Ср', long: 'Среда', on: 'в среду' },
  { day: 4, short: 'Чт', long: 'Четверг', on: 'в четверг' },
  { day: 5, short: 'Пт', long: 'Пятница', on: 'в пятницу' },
  { day: 6, short: 'Сб', long: 'Суббота', on: 'в субботу' },
  { day: 0, short: 'Вс', long: 'Воскресенье', on: 'в воскресенье' },
]

export function dayName(day: number): string {
  return WEEK.find((w) => w.day === day)?.long ?? '?'
}

export function dayOn(day: number): string {
  return WEEK.find((w) => w.day === day)?.on ?? '?'
}

const TIME_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?$/

/** Время в минутах от полуночи; null - не читается. */
export function minutesOf(t: string | undefined): number | null {
  const m = TIME_RE.exec((t ?? '').trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

/** HH:MM; нечитаемое время - как есть. */
export function clock(t: string | undefined): string {
  const m = minutesOf(t)
  if (m === null) return (t ?? '').trim()
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** Понедельник первым, внутри дня по началу. */
export function sortSlots<T extends GroupSchedule>(slots: T[]): T[] {
  return [...slots].sort(
    (a, b) => ((a.day_of_week + 6) % 7) - ((b.day_of_week + 6) % 7) || clock(a.start_time).localeCompare(clock(b.start_time)),
  )
}

/** Расписание одной строкой: порядок слотов и запись времени не важны (как на сервере). */
export function scheduleKey(slots: GroupSchedule[]): string {
  return slots
    .map((s) => `${s.day_of_week}|${clock(s.start_time)}|${clock(s.end_time)}|${(s.location ?? '').trim()}`)
    .sort()
    .join(';')
}

/** Слот в том виде, в каком его сохраняет сервер. */
export function normalizeSlot(s: GroupSchedule): GroupSchedule {
  const location = (s.location ?? '').trim()
  return {
    day_of_week: s.day_of_week,
    start_time: clock(s.start_time),
    end_time: clock(s.end_time),
    ...(location ? { location } : {}),
  }
}

export interface SlotCheck {
  /** Ошибки, с которыми слот не сохранить. */
  start?: string
  end?: string
  /** Пересечение с другими занятиями того же дня - предупреждение, не запрет. */
  overlaps: GroupSchedule[]
}

/**
 * Проверка одного слота среди остальных. Конец позже начала; два занятия с
 * одним началом в один день сервер не примет (занятие определяется группой,
 * датой и началом); пересечение по времени - только предупреждение: так
 * бывает, если в зале две части тренировки.
 */
export function checkSlot(slot: GroupSchedule, others: GroupSchedule[]): SlotCheck {
  const out: SlotCheck = { overlaps: [] }
  const start = minutesOf(slot.start_time)
  const end = minutesOf(slot.end_time)
  if (start === null) out.start = 'Укажите начало'
  if (end === null) out.end = 'Укажите конец'
  else if (start !== null && end <= start) out.end = 'Конец должен быть позже начала'
  if (start === null) return out

  for (const o of others) {
    if (o.day_of_week !== slot.day_of_week) continue
    const os = minutesOf(o.start_time)
    const oe = minutesOf(o.end_time)
    if (os === start) {
      out.start = `${dayName(slot.day_of_week)}: занятие с началом ${clock(o.start_time)} уже есть`
      continue
    }
    if (end !== null && os !== null && oe !== null && end > start && os < end && start < oe) out.overlaps.push(o)
  }
  return out
}

/** Пары занятий одного дня, которые пересекаются по времени. */
export function overlapsOf(slots: GroupSchedule[]): [GroupSchedule, GroupSchedule][] {
  const sorted = sortSlots(slots)
  const out: [GroupSchedule, GroupSchedule][] = []
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      const a = sorted[i]
      const b = sorted[j]
      if (a.day_of_week !== b.day_of_week) continue
      const [as, ae, bs, be] = [minutesOf(a.start_time), minutesOf(a.end_time), minutesOf(b.start_time), minutesOf(b.end_time)]
      if (as === null || ae === null || bs === null || be === null) continue
      if (as < be && bs < ae) out.push([a, b])
    }
  }
  return out
}

export function formatSlotTime(s: GroupSchedule): string {
  return `${clock(s.start_time)} - ${clock(s.end_time)}`
}
